import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { type CollectionUserGroupData } from '@frontend/api';
import { useDisclosure } from '@frontend/components';
import { noop } from '@frontend/utils';

import { broadcastService } from 'services/broadcastService';

import { useAppSelector } from 'store/hooks';
import { getActiveGroup } from 'store/session/selectors';

import { ConfirmChangeSiteDialog } from 'utils/multisite/ConfirmChangeSiteDialog';

import { useUserGroupLookup } from './hooks';
import { useSwitchGroup } from './hooks/useSwitchGroup';

type NextSiteProps = {
  userGroupId: number;
  nextUrl: string;
  groupBusinessName: string;
  skipConfirmation?: boolean;
};

type UserGroupCollectionContext = {
  availableGroups: CollectionUserGroupData[];
  userGroupsById: Record<number, CollectionUserGroupData>;
  getUserGroupById: (groupId: number) => CollectionUserGroupData | undefined;
  initChangeSite: (params: {
    nextUrl: string;
    userGroupId: number;
    skipConfirmation?: boolean;
  }) => void;
  getSupplierRolesById: (groupId: number) => Record<string, string>;
};

const UserGroupCollectionContext = createContext<UserGroupCollectionContext>({
  availableGroups: [],
  userGroupsById: {},
  getUserGroupById: () => undefined,
  initChangeSite: noop,
  getSupplierRolesById: () => ({}),
});

export const useUserGroupCollectionContext = () => {
  return useContext(UserGroupCollectionContext);
};

type UserGroupCollectionProviderProps = {
  children?: React.ReactNode;
};

export const UserGroupCollectionProvider = ({
  children,
}: UserGroupCollectionProviderProps) => {
  const confirmChangeSiteDialog = useDisclosure();

  const [nextSiteProps, setNextSiteProps] = useState<NextSiteProps | null>(
    null,
  );

  const {
    availableGroups,
    userGroupsById,
    getUserGroupById,
    getSupplierRolesById,
  } = useUserGroupLookup();
  const activeGroup = useAppSelector(getActiveGroup);

  const switchGroup = useSwitchGroup();

  const initChangeSite = useCallback(
    ({
      nextUrl,
      userGroupId,
      skipConfirmation,
    }: {
      nextUrl: string;
      userGroupId: number;
      skipConfirmation?: boolean;
    }) => {
      if (userGroupId === activeGroup?.id) {
        window.location.href = nextUrl;
        return;
      }

      if (broadcastService.peersCount === 0 || skipConfirmation) {
        switchGroup(userGroupId, nextUrl);
        return;
      }

      const group = getUserGroupById(userGroupId);
      setNextSiteProps({
        nextUrl,
        userGroupId,
        groupBusinessName: group?.businessName,
      });
      confirmChangeSiteDialog.onOpen();
    },
    [activeGroup?.id, confirmChangeSiteDialog, getUserGroupById, switchGroup],
  );

  const contextValue = useMemo(() => {
    return {
      availableGroups,
      userGroupsById,
      getUserGroupById,
      initChangeSite,
      getSupplierRolesById,
    };
  }, [
    availableGroups,
    userGroupsById,
    getUserGroupById,
    initChangeSite,
    getSupplierRolesById,
  ]);

  return (
    <UserGroupCollectionContext.Provider value={contextValue}>
      {children}
      {nextSiteProps && (
        <ConfirmChangeSiteDialog
          {...confirmChangeSiteDialog}
          {...nextSiteProps}
        />
      )}
    </UserGroupCollectionContext.Provider>
  );
};
